import isArabic from "is-arabic";
import { useEffect, useRef, useState } from "react";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Trash2, Edit, BarChart3, Users, Clock, CheckCheck, Eye, Share2, MessageCircleMore } from "lucide-react";
import { usePopUpContext } from "../contexts/Popup";
import { useLoaderContext } from "../contexts/loader";
import { useUserContext } from "../contexts/user";
import { useQuizzesContext } from "../contexts/Quizzes";
import copy from "copy-to-clipboard";
import { useCheckForNewToken } from "../checkForNewToken";

const QuizDetails = () => {
    const { id } = useParams();

    const navigate = useNavigate();

    const { checkForNewToken } = useCheckForNewToken();

    const [activeTab, setActiveTab] = useState("Overview");
    const [averageScore, setAverageScore] = useState(0);
    const [quizData, setQuizData] = useState({});
    const [results, setResults] = useState({});
    const [quizTime, setQuizTime] = useState();
    const [showUpdateContainer, setShowUpdateContainer] = useState(false);
    const [showUserResults, setShowUserResults] = useState(-1);
    const [showRemoveQuiz, setShowRemoveQuiz] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [scoreDistribution, setScoreDistribution] = useState({
        first: 0,
        second: 0,
        third: 0,
    })

    const { setMessageContent } = usePopUpContext();
    const { setIsLoading } = useLoaderContext();
    const { setQuizzes, quizzes } = useQuizzesContext();
    const { user, userInfo, userJWT, setUserJWT, setUserInfo, setUser, setUserIndex, appLanguage } = useUserContext();

    useEffect(() => {
        setIsLoading(prev => prev + 1);
        fetch("https://quiz-battle-api.vercel.app/api/results", {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${userJWT}`,
            },
            body: JSON.stringify({
                user_id: user,
                quizID: id,
            }),
        }).then(res => res.json())
            .then(data => {
                checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                if (!data.quizData) {
                    navigate("/home");
                    if (data.msg) {
                        setMessageContent({ title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied", message: data.msg, });
                    } else {
                        setMessageContent({ title: appLanguage === "ar" ? "فشل الجلب" : "Fetching Failed", message: appLanguage === "ar" ? "فشل في جلب بيانات هذا الاختبار من قاعدة البيانات" : "Failed to fetch this quiz data from the database", });
                    }
                } else {
                    const timeNeeded = data.quizData.questions.length * 30;
                    const minutes = Math.floor(timeNeeded / 60);
                    const seconds = timeNeeded % 60;
                    setQuizTime((minutes < 10 ? "0" + minutes : minutes) + ":" + (seconds < 10 ? "0" + seconds : seconds));
                    setQuizData(data.quizData);
                    setResults(data.studentsResults);
                }
            }).catch(err => {
                navigate("/home");
                setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                setMessageContent({
                    title: appLanguage === "ar" ? "فشل الجلب" : "Fetching Failed",
                    message: appLanguage === "ar" ? "فشل في جلب تفاصيل الاختبار الخاص بك من قاعدة البيانات." : "Failed to fetch your quiz details from the database.",
                })
            })
    }, [])

    useEffect(() => {
        if (quizData.category) {
            let totalStudentsResults = 0;
            const totalPoints = results.length * (quizData.questions.length * 5);
            const firstUsers = [];
            const secondUsers = [];
            const thirdUsers = [];
            for (let i = 0; i < results.length; i++) {
                const userResult = Math.round(results[i].points / (quizData.questions.length * 5) * 100);
                if (userResult >= 80) {
                    firstUsers.push(results[i]);
                } else if (userResult >= 50 && userResult < 80) {
                    secondUsers.push(results[i]);
                } else {
                    thirdUsers.push(results[i]);
                }
                totalStudentsResults += results[i].points;
            }
            setScoreDistribution({
                first: firstUsers.length ? Math.round(firstUsers.length / results.length * 100) : 0,
                second: secondUsers.length ? Math.round(secondUsers.length / results.length * 100) : 0,
                third: thirdUsers.length ? Math.round(thirdUsers.length / results.length * 100) : 0,
            });
            const averageScore = totalPoints ? Math.round(totalStudentsResults / totalPoints * 100) : 0;
            setAverageScore(averageScore);
        }
    }, [quizData])

    const getResultColor = (points) => {
        const score = Math.round(points / (quizData.questions.length * 5) * 100);
        switch (true) {
            case score >= 80:
                return "#0b0";
            case score >= 50:
                return "#f90";
            case score < 50:
                return "#f00";
        }
    }

    return (
        <div className={"quiz_details_container"}>
            <header>
                <ChevronLeft onClick={() => navigate("/home")} />
                <p>{appLanguage === "ar" ? "تفاصيل الاختبار" : "Quiz Details"}</p>
            </header>
            <div className="top_content">
                <div className="top">
                    <p>{quizData.category}</p>
                    <div className="right_side">
                        <button className="share" onClick={() => { copy(window.location.origin + "/info/" + id); setTimeout(() => { copy(window.location.origin + "/info/" + id) }, 1); setMessageContent({ title: appLanguage === "ar" ? "تم نسخ الرابط" : "Link Copied", message: appLanguage === "ar" ? "تم نسخ رابط الاختبار! ✅" : "Quiz link has been copied! ✅", }) }}><Share2 size={18} /></button>
                        <button className="edit" onClick={() => setShowUpdateContainer(true)}><Edit size={18} /></button>
                        <button className="remove" onClick={() => setShowRemoveQuiz(true)}><Trash2 size={18} /></button>
                    </div>
                </div>
                <div className="details">
                    <div className="item">
                        <div className="icon"><BarChart3 /></div>
                        <div className="text">
                            <div className="title">{appLanguage === "ar" ? "إجمالي الأسئلة" : "Total Questions"}</div>
                            <div className="value">{quizData.questions ? quizData.questions.length : 0}</div>
                        </div>
                    </div>
                    <div className="item">
                        <div className="icon"><CheckCheck /></div>
                        <div className="text">
                            <div className="title">{appLanguage === "ar" ? "إجمالي النقاط" : "Total Points"}</div>
                            <div className="value">{quizData.questions ? quizData.questions.length * 5 : 0}</div>
                        </div>
                    </div>
                    <div className="item">
                        <div className="icon"><BarChart3 /></div>
                        <div className="text">
                            <div className="title">{appLanguage === "ar" ? "متوسط الدرجات" : "Average Score"}</div>
                            <div className="value">{averageScore}%</div>
                        </div>
                    </div>
                    <div className="item">
                        <div className="icon"><Users /></div>
                        <div className="text">
                            <div className="title">{appLanguage === "ar" ? "إجمالي الطلاب" : "Total Students"}</div>
                            <div className="value">{results.length}</div>
                        </div>
                    </div>
                    <div className="item">
                        <div className="icon"><Clock /></div>
                        <div className="text">
                            <div className="title">{appLanguage === "ar" ? "إجمالي الوقت" : "Total Time"}</div>
                            <div className="value">{quizTime}</div>
                        </div>
                    </div>
                </div>
            </div>
            <button className="chat" onClick={() => navigate("/chat/" + id)}>{appLanguage === "ar" ? "دردشة" : "Chat"} <MessageCircleMore /></button>
            <div className="more_details">
                <div className="navigation_bar">
                    <button onClick={() => setActiveTab("Overview")} className={activeTab === "Overview" ? "active" : ""}>{appLanguage === "ar" ? "نظرة عامة" : "Overview"}</button>
                    <button onClick={() => setActiveTab("Questions")} className={activeTab === "Questions" ? "active" : ""}>{appLanguage === "ar" ? "الأسئلة" : "Questions"}</button>
                    <button onClick={() => setActiveTab("Results")} className={activeTab === "Results" ? "active" : ""}>{appLanguage === "ar" ? "النتائج" : "Results"}</button>
                    <button onClick={() => setActiveTab("Analytics")} className={activeTab === "Analytics" ? "active" : ""}>{appLanguage === "ar" ? "التحليلات" : "Analytics"}</button>
                </div>
                <div className="content">
                    {
                        activeTab === "Overview" ?
                            <div className="overview">
                                <div className="items">
                                    <div className="item">
                                        <div className="item_content">
                                            <p className="key">{appLanguage === "ar" ? "اسم المعلم:" : "Teacher Name:"}</p>
                                            <p className="value">{quizData.from_name}</p>
                                        </div>
                                    </div>
                                    <div className="item">
                                        <div className="item_content">
                                            <p className="key">{appLanguage === "ar" ? "المادة:" : "Subject:"}</p>
                                            <p className="value">{quizData.category ? quizData.category : (appLanguage === "ar" ? "جاري الجلب" : "Feching")}</p>
                                        </div>
                                    </div>
                                    <div className="item">
                                        <div className="item_content">
                                            <p className="key">{appLanguage === "ar" ? "عدد الأسئلة:" : "Questions Number:"}</p>
                                            <p className="value">{quizData.questions ? quizData.questions.length : 0}</p>
                                        </div>
                                    </div>
                                    <div className="item">
                                        <div className="item_content">
                                            <p className="key">{appLanguage === "ar" ? "وقت الاختبار:" : "Quiz Time:"}</p>
                                            <p className="value">{((quizData.questions ? quizData.questions.length : 0) * 30) / 60} {appLanguage === "ar" ? "دقائق" : "Minutes"}</p>
                                        </div>
                                    </div>
                                    <div className="item">
                                        <div className="item_content">
                                            <p className="key">{appLanguage === "ar" ? "إجمالي النقاط:" : "Total Points:"}</p>
                                            <p className="value">{(quizData.questions ? quizData.questions.length : 0) * 5} </p>
                                        </div>
                                    </div>
                                    <div className="description">
                                        <p className="title">{appLanguage === "ar" ? "الوصف" : "Description"}</p>
                                        <p className="value" dir={quizData.description ? (isArabic(quizData.description.split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{quizData.description ? quizData.description : (appLanguage === "ar" ? "لم يتم تقديم وصف" : "No Description Provided")}</p>
                                    </div>
                                </div>
                            </div>
                            :
                            (activeTab === "Questions" ?
                                <div className="questions_container">
                                    <div className="questions">
                                        {
                                            quizData.questions && quizData.questions.map((question, i) => {
                                                return (
                                                    <div className="question" key={"question_" + i}>
                                                        <div className="question_index">{i + 1}</div>
                                                        <div className="question_content">
                                                            <p className="question_text" dir={question.question ? (isArabic(question.question.split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.question}</p>
                                                            <div className="answers">
                                                                <button className={question.answers[0] === question.correctAnswer ? "active" : ""} dir={question.answers ? (isArabic(question.answers[0].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[0]}</button>
                                                                <button className={question.answers[1] === question.correctAnswer ? "active" : ""} dir={question.answers ? (isArabic(question.answers[1].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[1]}</button>
                                                                <button className={question.answers[2] === question.correctAnswer ? "active" : ""} dir={question.answers ? (isArabic(question.answers[2].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[2]}</button>
                                                                <button className={question.answers[3] === question.correctAnswer ? "active" : ""} dir={question.answers ? (isArabic(question.answers[3].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[3]}</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                                : (
                                    activeTab === "Results" ?
                                        <div className="results_container">
                                            {
                                                showUserResults !== -1 ?
                                                    <div className="questions">
                                                        <button className="back" onClick={() => setShowUserResults(-1)}><ChevronLeft /> Back</button>
                                                        {
                                                            quizData.questions.map((question, i) => {
                                                                return (
                                                                    <div className="question" key={"question_" + i}>
                                                                        {results[showUserResults].answers[i] === ' ' || results[showUserResults].answers[i].empty ? <span>{appLanguage === "ar" ? "لا توجد إجابة" : "No Answer"}</span> : undefined}
                                                                        <p className="question_text" dir={question.question ? (isArabic(question.question.split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.question}</p>
                                                                        <div className="answers">
                                                                            <button className={question.answers[0] === question.correctAnswer ? "active" : (question.answers[0] === results[showUserResults].answers[i] ? "red-active" : "")} dir={question.answers ? (isArabic(question.answers[0].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[0]}</button>
                                                                            <button className={question.answers[1] === question.correctAnswer ? "active" : (question.answers[1] === results[showUserResults].answers[i] ? "red-active" : "")} dir={question.answers ? (isArabic(question.answers[1].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[1]}</button>
                                                                            <button className={question.answers[2] === question.correctAnswer ? "active" : (question.answers[2] === results[showUserResults].answers[i] ? "red-active" : "")} dir={question.answers ? (isArabic(question.answers[2].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[2]}</button>
                                                                            <button className={question.answers[3] === question.correctAnswer ? "active" : (question.answers[3] === results[showUserResults].answers[i] ? "red-active" : "")} dir={question.answers ? (isArabic(question.answers[3].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{question.answers[3]}</button>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                    : <table>
                                                        <thead>
                                                            <tr>
                                                                <th>{appLanguage === "ar" ? "الاسم" : "Name"}</th>
                                                                <th>{appLanguage === "ar" ? "النتيجة" : "Result"}</th>
                                                                <th>{appLanguage === "ar" ? "إجراءات" : "Actions"}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                results.length ?
                                                                    results.map((result, i) => {
                                                                        return (
                                                                            <tr key={"result_" + i}>
                                                                                <td className="name">{result.name}</td>
                                                                                <td className="result" style={{ color: getResultColor(result.points), }}>{Math.round((result.points / (quizData.questions.length * 5)) * 100)}%</td>
                                                                                <td className="actions"><button onClick={() => setShowUserResults(i)}><Eye /></button></td>
                                                                            </tr>
                                                                        )
                                                                    })
                                                                    : <tr>
                                                                        <td className={"not-played"} colSpan={4}>{appLanguage === "ar" ? "لم يتم اللعب بعد" : "Not Played Yet"}</td>
                                                                    </tr>
                                                            }
                                                        </tbody>
                                                    </table>
                                            }
                                        </div> : <div className="analytics_container">
                                            <div className="score_distribution">
                                                <p className="title">{appLanguage === "ar" ? "توزيع الدرجات" : "Score Distribution"}</p>
                                                <div className="scores">
                                                    <div className="score first">
                                                        <p className="percentage">80-100%</p>
                                                        <div className="value"><span className="span_container"><span style={{ width: `${scoreDistribution.first}%`, }}></span></span> <p>{scoreDistribution.first}%</p></div>
                                                    </div>
                                                    <div className="score second">
                                                        <p className="percentage">50-79%</p>
                                                        <div className="value"><span className="span_container"><span style={{ width: `${scoreDistribution.second}%`, }}></span></span> <p>{scoreDistribution.second}%</p></div>
                                                    </div>
                                                    <div className="score third">
                                                        <p className="percentage">0-49%</p>
                                                        <div className="value"><span className="span_container"><span style={{ width: `${scoreDistribution.third}%`, }}></span></span> <p>{scoreDistribution.third}%</p></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="average_score">
                                                <p className="title">{appLanguage === "ar" ? "متوسط الدرجات" : "Average Score"}</p>
                                                <div className="content">
                                                    <CircularProgressbar styles={buildStyles({
                                                        pathColor: "#00f1a1",
                                                        textColor: "#FFF",
                                                        trailColor: "#FFFFFF33",
                                                    })} text={averageScore + "%"} value={averageScore} />
                                                </div>
                                            </div>
                                        </div>
                                )
                            )
                    }
                </div>
            </div>
            {showChangePassword ? <ChangePassword setIsLoading={setIsLoading} userID={user} setMessageContent={setMessageContent} setQuizData={setQuizData} setQuizzes={setQuizzes} quizzes={quizzes} setShowChangePassword={setShowChangePassword} /> : undefined}
            {showRemoveQuiz ? <DeleteQuiz quizID={id} userID={user} setShowRemoveQuiz={setShowRemoveQuiz} setMessageContent={setMessageContent} setQuizzes={setQuizzes} quizzes={quizzes} setIsLoading={setIsLoading} navigate={navigate} /> : undefined}
            {showUpdateContainer ? <UpdateQuiz category={quizData.category} description={quizData.description} quizData={quizData} setIsLoading={setIsLoading} setMessageContent={setMessageContent} setShowUpdateContainer={setShowUpdateContainer} quizID={id} userID={user} setQuizData={setQuizData} setQuizzes={setQuizzes} quizzes={quizzes} teacherName={userInfo.username} mrOrMrs={localStorage.getItem("teacher data") ? JSON.parse(localStorage.getItem("teacher data")).gender : "Mr." + userInfo.username} setShowChangePassword={setShowChangePassword} /> : undefined}
        </div>
    );
}

const UpdateQuiz = ({ description, category, setIsLoading, setMessageContent, setShowUpdateContainer, quizID, userID, quizData, setQuizData, setQuizzes, quizzes, teacherName, mrOrMrs, setShowChangePassword }) => {
    const navigate = useNavigate();

    const [quizDescription, setDescription] = useState(description);
    const [quizCategory, setQuizCategory] = useState(category)
    const [teacherGender, setTeacherGender] = useState(mrOrMrs);

    const { userJWT, setUserJWT, setUserInfo, setUserIndex, setUser, appLanguage } = useUserContext();

    const { checkForNewToken } = useCheckForNewToken();

    const categories = ["Math", "English", "Arabic", "Science", "Social Studies", "French"];

    const updateQuiz = () => {
        if (description === quizDescription && category === quizCategory && teacherGender === mrOrMrs) {
            setMessageContent({
                title: appLanguage === "ar" ? "لم يتغير شيء" : "Nothing Changed",
                message: appLanguage === "ar" ? "لا توجد تغييرات للتحديث." : "There are no changes to update.",
            });
            setShowUpdateContainer(false);
        } else {
            if (categories.includes(quizCategory)) {
                setIsLoading(prev => prev + 1);
                fetch("https://quiz-battle-api.vercel.app/api/edit", {
                    method: "PUT",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${userJWT}`,
                    },
                    body: JSON.stringify({
                        data: {
                            category: quizCategory,
                            description: quizDescription,
                            from_name: teacherGender,
                        },
                        quizID: quizID,
                        userID: userID,
                    }),
                }).then(res => res.json())
                    .then(data => {
                        checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                        setIsLoading(prev => prev !== 0 ? prev - 1 : prev);

                        if (data.failed || data.ok === false) {
                            setMessageContent({
                                title: appLanguage === "ar" ? "فشل التحديث" : "Failed to update",
                                message: data.msg,
                            })
                        } else {
                            const quizIndex = quizzes.findIndex(quiz => quiz._id === quizID);

                            if (quizIndex !== -1) {
                                const appQuizzes = [...quizzes];
                                appQuizzes[quizIndex] = { ...appQuizzes[quizIndex], category: quizCategory, description: quizDescription, from_name: teacherGender, }
                                setQuizzes(appQuizzes);
                            }

                            setQuizData(prev => ({
                                ...prev,
                                category: quizCategory,
                                description: quizDescription,
                                from_name: teacherGender,
                            }))
                            localStorage.setItem("teacher data", JSON.stringify({ gender: teacherGender, category: quizCategory, }))
                            setShowUpdateContainer(false);
                        }
                    }).catch(err => {
                        setIsLoading(prev => prev !== 0 ? prev - 1 : prev);

                        setMessageContent({
                            title: appLanguage === "ar" ? "فشل التحديث" : "Failed to update",
                            message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء تحديث الاختبار الخاص بك، حاول مرة أخرى لاحقاً." : "Oops! something wrong occured while updating your quiz, try again later.",
                        })
                    })
            } else {
                setMessageContent({
                    title: appLanguage === "ar" ? "إدخال غير صالح" : "Invalid Input",
                    message: appLanguage === "ar" ? "تحقق من مدخلاتك مرة أخرى لأن هناك خطأ ما." : "Check your inputs again because there is something wrong.",
                })
            }
        }
    }

    return (
        <div className="update_quiz_container">
            <div className="content">
                <p className="title">{appLanguage === "ar" ? "تحديث الاختبار" : "Update Quiz"}</p>
                <div className="update_container">
                    <div className="field">
                        <select onChange={(e) => setTeacherGender(e.target.value)} value={teacherGender}>
                            <option value={"Mr." + teacherName}>Mr.{teacherName}</option>
                            <option value={"Mrs." + teacherName}>Mrs.{teacherName}</option>
                        </select>
                    </div>
                    <div className="field">
                        <select onChange={(e) => setQuizCategory(e.target.value)} value={quizCategory}>
                            <option value="Science">Science</option>
                            <option value="English">English</option>
                            <option value="Math">Math</option>
                            <option value="Arabic">Arabic</option>
                            <option value="Social Studies">Social Studies</option>
                            <option value="French">French</option>
                        </select>
                    </div>
                    <div className="field">
                        <div className="title">{appLanguage === "ar" ? "الوصف" : "Description"}</div>
                        <textarea placeholder="Enter your quiz description" name="" id="" onChange={(e) => setDescription(e.target.value)} value={quizDescription}></textarea>
                    </div>
                    <button className="password_button" onClick={() => setShowChangePassword(true)}>{quizData.password ? (appLanguage === "ar" ? "تغيير كلمة المرور" : "Change Password") : (appLanguage === "ar" ? "إضافة كلمة مرور" : "Add Password")}</button>
                    <div className="buttons">
                        <button className="update" onClick={updateQuiz}>{appLanguage === "ar" ? "تحديث" : "Update"}</button>
                        <button className="cancel" onClick={() => setShowUpdateContainer(false)}>{appLanguage === "ar" ? "إلغاء" : "Cancel"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const DeleteQuiz = ({ quizID, userID, setShowRemoveQuiz, setMessageContent, setIsLoading, navigate, quizzes, setQuizzes }) => {
    const [inputText, setInputText] = useState("");

    const { userJWT, setUserJWT, setUser, setUserInfo, setUserIndex, appLanguage } = useUserContext();

    const { checkForNewToken } = useCheckForNewToken();

    const removeQuiz = () => {
        setIsLoading(prev => prev + 1);
        fetch("https://quiz-battle-api.vercel.app/api/delete", {
            method: "DELETE",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${userJWT}`,
            },
            body: JSON.stringify({
                userID: userID,
                quizID: quizID,
            }),
        }).then(res => res.json())
            .then(data => {
                checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                setIsLoading(prev => prev !== 0 ? prev - 1 : prev);

                if (data.failed || data.ok === false) {
                    setMessageContent({
                        title: appLanguage === "ar" ? "فشل الحذف" : "Failed To Remove",
                        message: data.msg,
                    })
                } else {
                    navigate("/home");
                    setMessageContent({
                        title: appLanguage === "ar" ? "تم حذف الاختبار" : "Quiz Removed",
                        message: appLanguage === "ar" ? "تم حذف الاختبار بنجاح!" : "Quiz removed successfully!",
                    })
                    const newQuizzes = [...quizzes];
                    const quizIndex = newQuizzes.findIndex(quiz => quiz._id === quizID)
                    newQuizzes.splice(quizIndex, 1);
                    setQuizzes(newQuizzes)
                }
            })
    }

    return (
        <div className="delete_quiz_container">
            <div className="content">
                <p className="title">{appLanguage === "ar" ? "حذف الاختبار" : "Remove Quiz"}</p>
                <div className="delete_content">
                    <p className="text">{appLanguage === "ar" ? "أدخل الكلمة" : "Enter the word"} <span>"remove"</span> {appLanguage === "ar" ? "في الحقل أدناه لتأكيد الحذف." : "in the input below to confirm your deletion."}</p>
                    <input type="text" placeholder="Remove Quiz" onChange={(e) => setInputText(e.target.value)} />
                    <div className="buttons">
                        <button onClick={() => { inputText === "remove" ? removeQuiz() : console.log("Can't delete quiz now.") }} className={"delete " + (inputText === "remove" ? "" : "disabled")}>{appLanguage === "ar" ? "حذف" : "Remove"}</button>
                        <button className="cancel" onClick={() => setShowRemoveQuiz(false)}>{appLanguage === "ar" ? "إلغاء" : "Cancel"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const ChangePassword = ({ setIsLoading, userID, setMessageContent, setQuizData, setQuizzes, quizzes, setShowChangePassword }) => {
    const { id } = useParams();

    const navigate = useNavigate();
    const passwordInput = useRef();

    const { userJWT, setUserJWT, setUser, setUserIndex, setUserInfo, appLanguage } = useUserContext();

    const { checkForNewToken } = useCheckForNewToken();

    const updatePassword = () => {
        setIsLoading(true);
        fetch("https://quiz-battle-api.vercel.app/api/change-password", {
            method: "PUT",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${userJWT}`,
            },
            body: JSON.stringify({
                newPassword: passwordInput.current.value,
                userID: userID,
                quizID: id,
            }),
        }).then(res => res.json())
            .then(data => {
                checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                setIsLoading(false);
                if (data.failed || data.ok === false) {
                    setMessageContent({
                        title: appLanguage === "ar" ? "فشل التحديث" : "Updating Failed",
                        message: data.msg,
                    })
                } else {
                    if (data.changed) {
                        setMessageContent({
                            title: appLanguage === "ar" ? "تم تغيير كلمة المرور" : "Password Changed",
                            message: appLanguage === "ar" ? "تم تغيير كلمة المرور بنجاح ✅." : "Password has been changed successfully ✅.",
                        })
                        setShowChangePassword(false);
                        let newQuizzes = [...quizzes].map(quiz => {
                            const newQuiz = { ...quiz };
                            if (newQuiz._id === id) {
                                newQuiz.password = data.password;
                            }
                            return newQuiz;
                        });
                        setQuizzes(newQuizzes);
                        setQuizData(prev => ({
                            ...prev, password: data.password,
                        }))
                    }
                }
            }).catch(() => {
                setIsLoading(false);
                setMessageContent({
                    title: appLanguage === "ar" ? "فشل التحديث" : "Updating Failed",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء تحديث كلمة مرور الاختبار." : "Oops! something went wrong while updating quiz password.",
                })
            })
    }

    return (
        <div className="enter_password_container">
            <div className="content">
                <p className="title">{appLanguage === "ar" ? "تغيير كلمة المرور" : "Change Password"}</p>
                <p className="note">{appLanguage === "ar" ? "إذا كنت تريد إزالة كلمة المرور اترك الحقل فارغاً." : "If you want to remove the password leave the input empty."}</p>
                <div className="main_content">
                    <input ref={passwordInput} type="text" placeholder={appLanguage === "ar" ? "كلمة المرور الجديدة..." : 'New Password...'} />
                    <button className="start" onClick={updatePassword}>{appLanguage === "ar" ? "تغيير كلمة المرور" : "Change Password"}</button>
                    <button className="cancel" onClick={() => setShowChangePassword(false)}>{appLanguage === "ar" ? "إلغاء" : "Cancel"}</button>
                </div>
            </div>
        </div>
    );
}

export default QuizDetails;